/**
 * Add User Dialog - Creates a user invitation for a specific customer company.
 * If the customer belongs to a group, shows site (company) selection for access control.
 */

import { useState, useEffect, useRef } from 'react';
import { invitationAPI, coreAPI, type UserInvitationCreate, type Role, type Site } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Checkbox } from '../../components/ui/checkbox';
import { UserPlus, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

interface AddUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: number;
  companyName: string;
  groupId?: number | null;
  onUserAdded: () => void;
}

const DEPARTMENTS = [
  { value: 'admin', label: 'Admin (Full Access)' },
  { value: 'purchasing', label: 'Sales Orders' },
  { value: 'quality', label: 'Quality' },
  { value: 'finance', label: 'Finance' },
  { value: 'operations', label: 'Operations' },
  { value: 'logistics', label: 'Logistics' },
  { value: 'engineering', label: 'Engineering' },
];

const INITIAL_FORM: UserInvitationCreate = {
  email: '',
  first_name: '',
  last_name: '',
  company: 0,
  role: null,
  job_title: '',
  department: '',
  site_ids: [],
  message: '',
};

export function AddUserDialog({
  open,
  onOpenChange,
  companyId,
  companyName,
  groupId,
  onUserAdded,
}: AddUserDialogProps) {
  const [formData, setFormData] = useState<UserInvitationCreate>({ ...INITIAL_FORM, company: companyId });
  const [roles, setRoles] = useState<Role[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Which button submitted the form: true = "Send Invitation" (email now),
  // false = "Add Without Sending" (create the record, send the email later).
  const sendEmailRef = useRef(true);

  useEffect(() => {
    if (open && companyId) {
      loadOptions();
      setFormData({ ...INITIAL_FORM, company: companyId });
      setError(null);
    }
  }, [open, companyId]);

  const loadOptions = async () => {
    setLoadingOptions(true);
    // Load roles and sites INDEPENDENTLY — a failing sites fetch must not
    // wipe out the roles dropdown (and vice versa).
    try {
      const rolesRes = await coreAPI.getRoles({ company_type: 'customer', is_active: true });
      setRoles(rolesRes.data.results || []);
    } catch (err: unknown) {
      console.error('Error loading roles:', err);
      setRoles([]);
    }

    if (groupId) {
      try {
        const sitesRes = await coreAPI.getSites({ group: groupId, is_active: true });
        const groupSites = sitesRes.data.results || [];
        setSites(groupSites);
        setFormData(prev => ({ ...prev, site_ids: groupSites.map(s => s.id) }));
      } catch (err: unknown) {
        console.error('Error loading sites:', err);
        setSites([]);
      }
    } else {
      setSites([]);
    }
    setLoadingOptions(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const sendEmail = sendEmailRef.current;
    setLoading(true);
    setError(null);

    try {
      await invitationAPI.createInvitation({ ...formData, company: companyId, send_email: sendEmail });
      toast.success(sendEmail
        ? 'Invitation sent successfully'
        : 'User added — invitation can be sent later');
      onOpenChange(false);
      onUserAdded();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { email?: string[]; error?: string } } };
      setError(
        axiosErr.response?.data?.email?.[0] ||
        axiosErr.response?.data?.error ||
        (sendEmail
          ? 'Failed to send invitation. Please try again.'
          : 'Failed to add user. Please try again.')
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSiteToggle = (siteId: number) => {
    setFormData(prev => ({
      ...prev,
      site_ids: prev.site_ids?.includes(siteId)
        ? prev.site_ids.filter(id => id !== siteId)
        : [...(prev.site_ids || []), siteId],
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add User to {companyName}</DialogTitle>
          <DialogDescription>
            Send an invitation to join as a user for this customer.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {loadingOptions ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="add-first-name">First Name *</Label>
                <Input
                  id="add-first-name"
                  value={formData.first_name}
                  required
                  placeholder="John"
                  onChange={e => setFormData({ ...formData, first_name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="add-last-name">Last Name *</Label>
                <Input
                  id="add-last-name"
                  value={formData.last_name}
                  required
                  placeholder="Doe"
                  onChange={e => setFormData({ ...formData, last_name: e.target.value })}
                />
              </div>
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="add-email">Email Address *</Label>
              <Input
                id="add-email"
                type="email"
                value={formData.email}
                required
                placeholder="john.doe@company.com"
                onChange={e => setFormData({ ...formData, email: e.target.value })}
              />
            </div>

            {/* Department & Role */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Department</Label>
                <Select
                  value={formData.department || 'none'}
                  onValueChange={v => setFormData({ ...formData, department: v === 'none' ? '' : v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {DEPARTMENTS.map(d => (
                      <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <Select
                  value={formData.role?.toString() || 'none'}
                  onValueChange={v => setFormData({ ...formData, role: v === 'none' ? null : parseInt(v) })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No specific role</SelectItem>
                    {roles.map(r => (
                      <SelectItem key={r.id} value={r.id.toString()}>{r.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Job Title */}
            <div className="space-y-2">
              <Label htmlFor="add-job-title">Job Title</Label>
              <Input
                id="add-job-title"
                value={formData.job_title}
                placeholder="e.g., Sales Manager"
                onChange={e => setFormData({ ...formData, job_title: e.target.value })}
              />
            </div>

            {/* Company Access - only shown when customer belongs to a group */}
            {sites.length > 0 && (
              <div className="space-y-2">
                <Label>Company Access</Label>
                <p className="text-xs text-muted-foreground">
                  Select which sites this user can access.
                </p>
                <div className="grid grid-cols-1 gap-2 max-h-32 overflow-y-auto border rounded-md p-3">
                  {sites.map(site => (
                    <div key={site.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`add-site-${site.id}`}
                        checked={formData.site_ids?.includes(site.id)}
                        onCheckedChange={() => handleSiteToggle(site.id)}
                      />
                      <label htmlFor={`add-site-${site.id}`} className="text-sm cursor-pointer">
                        {site.name} {site.city ? `(${site.city})` : ''}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
                Cancel
              </Button>
              {/* Adds the user without emailing — the invitation can be sent later. */}
              <Button
                type="submit"
                variant="outline"
                disabled={loading}
                onClick={() => { sendEmailRef.current = false; }}
              >
                <UserPlus className="h-4 w-4 mr-2" />Add Without Sending
              </Button>
              <Button
                type="submit"
                disabled={loading}
                onClick={() => { sendEmailRef.current = true; }}
              >
                {loading ? (
                  <><Loader2 className="h-4 w-4 animate-spin mr-2" />Saving...</>
                ) : (
                  <><UserPlus className="h-4 w-4 mr-2" />Send Invitation</>
                )}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
